import { CLASS_GROUPS } from "#constants/classOptions"
import {
  ALL_SKILL_LEVELS,
  CLASS_GROUP_TO_SKILL_TYPE,
  type CoreSkillLevel,
  type DigitalSkillLevel,
  type SculptureSkillLevel,
} from "#constants/skillLevelConstants"
import {
  EVALUATION_STATUS,
  type NoPromoReason,
} from "#customTypes/composedTypes/evaluationTypes"
import {
  type StudentSkillHistory,
  StudentSkillType,
} from "#customTypes/composedTypes/studentTypes"
import type { Uuid } from "#customTypes/globalTypes"
import { addOneSemester } from "#utils/semesterHelpers"

export type PromotionTag = "Promoted" | "Not promoted" | "No eval"

const EVAL_ID_FIELD = {
  [StudentSkillType.core]: "coreEvalId",
  [StudentSkillType.digital]: "digitalEvalId",
  [StudentSkillType.sculpture]: "sculptureEvalId",
} as const

export const ladderFor = (classGroup: CLASS_GROUPS): string[] =>
  Object.keys(ALL_SKILL_LEVELS[classGroup])

export const isTopOfLadder = (
  skill: string,
  classGroup: CLASS_GROUPS
): boolean => {
  const ladder = ladderFor(classGroup)
  const index = ladder.indexOf(skill)
  return index !== -1 && index === ladder.length - 1
}

export const isPromoted = (
  currentSkill: string | undefined,
  nextSkill: string | undefined,
  classGroup: CLASS_GROUPS
): boolean => {
  if (!currentSkill || !nextSkill) return false
  const ladder = ladderFor(classGroup)
  const currentIndex = ladder.indexOf(currentSkill)
  const nextIndex = ladder.indexOf(nextSkill)
  if (currentIndex === -1 || nextIndex === -1) return false
  return nextIndex > currentIndex
}

/** Same or lower level, except an automatic stay on the top rung. */
export const showsHoldReason = (
  currentSkill: string | undefined,
  nextSkill: string | undefined,
  classGroup: CLASS_GROUPS
): boolean => {
  if (!currentSkill || !nextSkill) return false
  if (isPromoted(currentSkill, nextSkill, classGroup)) return false
  const ladder = ladderFor(classGroup)
  if (ladder.indexOf(currentSkill) === -1 || ladder.indexOf(nextSkill) === -1) {
    return false
  }
  return !(
    isTopOfLadder(currentSkill, classGroup) && currentSkill === nextSkill
  )
}

/**
 * Evaluations do not store isDirty. Status is the pending-work flag.
 * Submit while the row has not been sent, or it was edited or rejected.
 */
export const promotionNeedsSubmit = (
  status: EVALUATION_STATUS | undefined
): boolean =>
  status == null ||
  status === EVALUATION_STATUS.NOT_STARTED ||
  status === EVALUATION_STATUS.IN_PROGRESS ||
  status === EVALUATION_STATUS.REJECTED

/** Approve only while a submission is waiting. An approved row is not dirty. */
export const promotionNeedsApprove = (
  status: EVALUATION_STATUS | undefined
): boolean => status === EVALUATION_STATUS.SUBMITTED

/**
 * Row wash for a finished review. Printed counts as approved.
 * Anything still waiting stays uncolored.
 */
export const promotionReviewTone = (
  status: EVALUATION_STATUS | undefined
): "approved" | "rejected" | undefined => {
  if (
    status === EVALUATION_STATUS.APPROVED ||
    status === EVALUATION_STATUS.PRINTED
  ) {
    return "approved"
  }
  if (status === EVALUATION_STATUS.REJECTED) return "rejected"
  return undefined
}

export const canSubmitPromotion = ({
  currentSkill,
  nextSkill,
  classGroup,
  status,
  noPromoReason,
}: {
  currentSkill?: string
  nextSkill?: string
  classGroup: CLASS_GROUPS
  status?: EVALUATION_STATUS
  noPromoReason?: NoPromoReason[]
}): boolean => {
  if (status === EVALUATION_STATUS.NO_EVAL) return false
  if (!currentSkill || !nextSkill) return false
  if (!showsHoldReason(currentSkill, nextSkill, classGroup)) return true
  return (noPromoReason?.length ?? 0) > 0
}

/** Why Submit is disabled, or undefined when the row can be submitted. */
export const promotionSubmitTooltip = ({
  currentSkill,
  nextSkill,
  classGroup,
  status,
  noPromoReason,
}: {
  currentSkill?: string
  nextSkill?: string
  classGroup: CLASS_GROUPS
  status?: EVALUATION_STATUS
  noPromoReason?: NoPromoReason[]
}): string | undefined => {
  if (!promotionNeedsSubmit(status)) return "No changes to submit"
  if (!currentSkill) return "Set a current level first"
  if (!nextSkill) return "Set a next level first"
  if (
    showsHoldReason(currentSkill, nextSkill, classGroup) &&
    (noPromoReason?.length ?? 0) === 0
  ) {
    return "Add a hold reason before submitting"
  }
  return undefined
}

export const getPromotionTag = (
  status: EVALUATION_STATUS | undefined,
  currentSkill: string | undefined,
  nextSkill: string | undefined,
  classGroup: CLASS_GROUPS
): PromotionTag => {
  if (status === EVALUATION_STATUS.NO_EVAL) return "No eval"
  if (isPromoted(currentSkill, nextSkill, classGroup)) return "Promoted"
  return "Not promoted"
}

const skillEntry = (
  classGroup: CLASS_GROUPS,
  level: string,
  evalId: Uuid
): Partial<StudentSkillHistory[string]> => {
  const skillType = CLASS_GROUP_TO_SKILL_TYPE[classGroup]
  if (skillType === StudentSkillType.digital) {
    return {
      digital: level as DigitalSkillLevel,
      digitalEvalId: evalId,
    }
  }
  if (skillType === StudentSkillType.sculpture) {
    return {
      sculpture: level as SculptureSkillLevel,
      sculptureEvalId: evalId,
    }
  }
  return {
    core: level as CoreSkillLevel,
    coreEvalId: evalId,
  }
}

const skillHistoryPath = (semester: string, field: string) =>
  `skillHistory.${semester}.${field}`

/** Field paths under the student doc. Writes do not replace `skillHistory`. */
export const buildNextSemesterSkillPatch = ({
  semester,
  classGroup,
  nextSkill,
  evalId,
}: {
  semester: string
  classGroup: CLASS_GROUPS
  nextSkill: string
  evalId: Uuid
}): { writes: Record<string, string> } | { skipped: true } => {
  const nextSemester = addOneSemester(semester)
  if (!nextSemester) return { skipped: true }

  const writes: Record<string, string> = {}
  for (const [field, value] of Object.entries(
    skillEntry(classGroup, nextSkill, evalId)
  )) {
    if (typeof value !== "string") continue
    writes[skillHistoryPath(nextSemester, field)] = value
  }
  return { writes }
}

/** A skipped or unchanged patch does not touch the student document. */
export const skillPatchTouchesStudent = (
  patch:
    | { writes: Record<string, string> }
    | { skipped: true }
    | { deletes: string[] }
    | { unchanged: true }
): boolean => "writes" in patch || "deletes" in patch

/**
 * Deletes only this eval's next-semester skill fields. A missing next semester
 * is unchanged: there is nothing to remove, and the caller still records NO_EVAL.
 */
export const buildNoEvalSkillPatch = ({
  skillHistory,
  semester,
  classGroup,
  evalId,
}: {
  skillHistory: StudentSkillHistory | undefined
  semester: string
  classGroup: CLASS_GROUPS
  evalId: Uuid
}): { deletes: string[] } | { unchanged: true } => {
  const nextSemester = addOneSemester(semester)
  if (!nextSemester) return { unchanged: true }

  const entry = skillHistory?.[nextSemester]
  if (!entry) return { unchanged: true }

  const skillType = CLASS_GROUP_TO_SKILL_TYPE[classGroup]
  const evalIdField = EVAL_ID_FIELD[skillType]
  if (entry[evalIdField] !== evalId) return { unchanged: true }

  return {
    deletes: [
      skillHistoryPath(nextSemester, skillType),
      skillHistoryPath(nextSemester, evalIdField),
    ],
  }
}
